import { Flex, Paper, Text, Badge, Box, SimpleGrid, Stack } from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconTool,
  IconShieldCheck,
  IconBriefcase,
  IconTarget,
  IconUserCheck,
  IconUser,
} from "@tabler/icons-react";
import ProductsServicesTab from "./ProductsServicesTab";
import {
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getIndustryLabel,
  getSubIndustryLabel,
  getAdditionalTechInfraLabel,
  regionsData,
} from "../constants/general.const";

interface ProfileTabProps {
  company: any;
  collaboration?: any;
}

export default function ProfileTab({ company, collaboration }: ProfileTabProps) {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };
  const metadata = company.externalVerificationLogs?.[0]?.metadata;

  return (
    <>
      {/* Collaboration Intent Details */}
      {collaboration?.requesterRole && (
        <Paper
          p={0}
          radius="lg"
          mb="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconBriefcase size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              Collaboration Intent Details
            </Text>
          </Flex>
          <Box p={{ base: "lg", sm: "xl" }}>
            <Text size="xs" c="slate.5" mb="lg">
              Information provided by the company about their collaboration goals
            </Text>

            <Flex direction="column" gap="md">
            {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0 && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconTarget size={16} color="var(--mantine-color-primary-6)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    Collaboration type they're exploring
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  {company.collaborationIntent.map((intent: string, i: number) => (
                    <Badge
                      key={i}
                      variant="light"
                      color="#E9D4FF"
                      bg="#FAF5FF"
                      c="#8200DB"
                      py={10}
                      px={8}
                      size="md"
                      fw={400}
                      style={{ border: "1px solid #E9D4FF" }}
                    >
                      {getCollaborationIntentLabel(intent)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            <Box>
              <Flex align="center" gap={6} mb="xs">
                <IconBriefcase size={16} color="var(--mantine-color-primary-6)" />
                <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                  Their role in this collaboration
                </Text>
              </Flex>
              <Flex gap="xs" wrap="wrap">
                <Badge
                  variant="light"
                  color="#B9F8CF"
                  bg="#F0FDF4"
                  c="#008236"
                  py={10}
                  px={8}
                  size="md"
                  fw={400}
                  style={{ border: "1px solid #B9F8CF" }}
                >
                  {getCollaborationRoleLabel(collaboration.requesterRole)}
                </Badge>
              </Flex>
            </Box>

            {collaboration.expectedPartnerRole && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconUserCheck size={16} color="var(--mantine-color-primary-6)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    What they expect from you
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  <Badge
                    variant="light"
                    color="#FEE685"
                    bg="#FFFBEB"
                    c="#BB4D00"
                    py={10}
                    px={8}
                    size="md"
                    fw={400}
                    style={{ border: "1px solid #FEE685" }}
                  >
                    {getCollaborationRoleLabel(collaboration.expectedPartnerRole)}
                  </Badge>
                </Flex>
              </Box>
            )}
            </Flex>
          </Box>
        </Paper>
      )}

      {/* Business Overview */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconBuilding size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Business Overview
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Flex gap="xl" wrap="wrap">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" fw={600} c="slate.8" tt="uppercase" mb="xs">
              COMPANY DESCRIPTION
            </Text>
            <Text size="sm" style={{ lineHeight: 1.5, wordBreak: "break-word", overflow: "hidden", textAlign: "justify" }}>{company.description || "N/A"}</Text>
          </Flex>

          {/* <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              YEARS OF OPERATION
            </Text>
            <Text size="sm">{getYearsOfOperation()}</Text>
          </Flex> */}

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="sm" c="slate.8" fw={700} mb="xs">
              TRADE NAME
            </Text>
            <Text size="sm">XXXX</Text>
          </Flex>

          <Flex style={{ flex: "1 1 100%" }} justify="space-between" gap="md" wrap="wrap">
            <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  INDUSTRY CATEGORY
                </Text>
                <Text size="sm">
                  {Array.isArray(company.industryCategory) && company.industryCategory.length > 0
                    ? company.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>

              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  SUB INDUSTRY
                </Text>
                <Text size="sm">
                  {Array.isArray(company.subIndustry) && company.subIndustry.length > 0
                    ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>
            </Flex>

            <Flex style={{ flex: "1 1 48%", minWidth: "280px" }} gap="md" wrap="wrap">
              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  WHO AM I
                </Text>
                <Text size="sm">
                  {Array.isArray(company.whoAmI) && company.whoAmI.length > 0
                    ? company.whoAmI.map((role: string) => getWhoAmILabel(role)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>

              <Flex direction="column" style={{ flex: "1 1 46%", minWidth: "220px" }}>
                <Text size="sm" c="slate.8" fw={700} mb="xs">
                  COLLABORATION INTENT
                </Text>
                <Text size="sm">
                  {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0
                    ? company.collaborationIntent.map((intent: string) => getCollaborationIntentLabel(intent)).join(", ")
                    : "N/A"}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        </Box>
      </Paper>

      {/* Products & Services */}
      <ProductsServicesTab company={company} />

      {/* Capabilities & Offerings */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconTool size={22} color="#ffc300" stroke={1.5} style={{ transform: "scaleX(-1)" }} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Capabilities & Offerings
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Flex gap="xl" wrap="wrap" mb="lg">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              CORE CAPABILITIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.coreCapabilities || []).map((capability: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {capability}
                </Badge>
              ))}
              {(!company.coreCapabilities || company.coreCapabilities.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>

          {company.secondaryCapabilities && company.secondaryCapabilities.length > 0 && (
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                SECONDARY CAPABILITIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.secondaryCapabilities.map((capability: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {capability}
                  </Badge>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        {company.keyDifferentiators && (
          <Flex direction="column" mb="lg">
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              KEY DIFFERENTIATORS
            </Text>
            <Text size="sm">{company.keyDifferentiators}</Text>
          </Flex>
        )}

        <Flex direction="column" mb="lg">
          <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
            TECHNOLOGY & INFRASTRUCTURE
          </Text>
          <Flex gap="xs" wrap="wrap">
            {(company.technologies || []).map((tech: string, index: number) => (
              <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                {tech}
              </Badge>
            ))}
            {(!company.technologies || company.technologies.length === 0) && (
              <Text size="sm" c="slate.6">
                N/A
              </Text>
            )}
          </Flex>
        </Flex>

        {company.additionalTechInfrastructure && company.additionalTechInfrastructure.length > 0 && (
          <Flex direction="column">
            <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
              ADDITIONAL TECHNOLOGY & INFRASTRUCTURE
            </Text>
            <Flex gap="xs" wrap="wrap">
              {company.additionalTechInfrastructure.map((item: string, index: number) => (
                <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                  {getAdditionalTechInfraLabel(item)}
                </Badge>
              ))}
            </Flex>
          </Flex>
        )}
        </Box>
      </Paper>

      {/* Geography & Markets */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconMapPin size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Geography & Markets
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
          <Flex gap="xl" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                OPERATING GEOGRAPHIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {(company.operatingCountries || []).map((country: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {country}
                  </Badge>
                ))}
                {(!company.operatingCountries || company.operatingCountries.length === 0) && (
                  <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                    N/A
                  </Text>
                )}
              </Flex>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                PREFERRED COLLABORATION REGIONS
              </Text>
              <Flex gap="xs" wrap="wrap">
                {(company.preferredCollaborationRegions || []).map((region: string, index: number) => (
                  <Badge key={index} style={{ backgroundColor: "#04305c", color: "white" }} py={14} px={12} size="md" fw={500}>
                    {getRegionLabel(region)}
                  </Badge>
                ))}
                {(!company.preferredCollaborationRegions || company.preferredCollaborationRegions.length === 0) && (
                  <Text size="sm" c="slate.6">
                    N/A
                  </Text>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Paper>

      {/* Contact Person - masked */}
      <Paper
        p={0}
        radius="lg"
        mb="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconUser size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            Contact Person
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
        <Box
          pt="md"
          pb="md"
          style={{
            borderTop: "1px solid #E5E7EB",
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <Flex gap="md" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                NAME
              </Text>
              <Text size="sm">XXXX</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                PHONE NUMBER
              </Text>
              <Text size="sm">XXXX</Text>
            </Flex>
            <Flex direction="column" style={{ flex: "1 1 220px", minWidth: "220px" }}>
              <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                DESIGNATION
              </Text>
              <Text size="sm">XXXX</Text>
            </Flex>
          </Flex>
        </Box>
        </Box>
      </Paper>

      {/* System Verification Details */}
      {metadata && (
        <Paper
          p={0}
          radius="lg"
          mb="xl"
          style={{
            overflow: "hidden",
            backgroundColor: "#ebf0ff",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconShieldCheck size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              System Verification Details
            </Text>
          </Flex>
          <Box p={{ base: "lg", sm: "xl" }}>
          <Flex direction="column" gap="lg">
            <Flex gap="xl" wrap="wrap">
              <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  COMPANY NAME
                </Text>
                <Text size="sm">{metadata.companyName ? "XXXX" : "-"}</Text>
              </Flex>
              <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  COMPANY ADDRESS
                </Text>
                <Text size="sm">{metadata.companyAddress ? "XXXX" : "-"}</Text>
              </Flex>
            </Flex>

            <Flex style={{ overflowX: "auto" }} gap="xl" wrap="nowrap" align="flex-start">
              <Flex direction="column" style={{ flex: "0 0 23%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  DATE OF INCORPORATION
                </Text>
                <Text size="sm">{metadata.dateOfIncorporation ? "XXXX" : "-"}</Text>
              </Flex>
              <Flex direction="column" style={{ flex: "0 0 23%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  REGISTRATION NUMBER
                </Text>
                <Text size="sm">{metadata.registrationNumber ? "XXXX" : "-"}</Text>
              </Flex>
              <Flex direction="column" style={{ flex: "0 0 23%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  COMPANY TYPE
                </Text>
                <Text size="sm">{metadata.companyType ? "XXXX" : "-"}</Text>
              </Flex>
              <Flex direction="column" style={{ flex: "0 0 23%", minWidth: "220px" }}>
                <Text size="md" c="slate.8" tt="uppercase" mb="sm" fw={600}>
                  COMPANY STATUS
                </Text>
                <Text size="sm">{metadata.companyStatus ? "XXXX" : "-"}</Text>
              </Flex>
            </Flex>
          </Flex>
          </Box>
        </Paper>
      )}

      {/* About Verified Listings */}
      <Paper
        p={0}
        radius="lg"
        mt="xl"
        style={{
          overflow: "hidden",
          backgroundColor: "#ebf0ff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Flex
          align="center"
          gap="sm"
          px={{ base: "md", sm: "xl" }}
          py={{ base: "md", sm: "lg" }}
          style={{ backgroundColor: "#04305c" }}
        >
          <IconBuilding size={22} color="#ffc300" stroke={1.5} />
          <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
            About Verified Listings
          </Text>
        </Flex>
        <Box p={{ base: "lg", sm: "xl" }}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={{ base: "lg", sm: "xl" }} verticalSpacing="md">
            <Stack gap="md">
              <Flex align="flex-start" gap="sm">
                <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                <Text size="sm" fw={700} lh={1.55} c="#04305c">
                  Full company details are visible after mutual interest is confirmed
                </Text>
              </Flex>
              <Flex align="flex-start" gap="sm">
                <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                <Text size="sm" fw={700} lh={1.55} c="#04305c">
                  Verification badge confirms company authenticity
                </Text>
              </Flex>
            </Stack>
            <Stack gap="md">
              <Flex align="flex-start" gap="sm">
                <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                <Text size="sm" fw={700} lh={1.55} c="#04305c">
                  Contact information becomes accessible to matched companies
                </Text>
              </Flex>
              <Flex align="flex-start" gap="sm">
                <Box mt={6} w={6} h={6} style={{ flexShrink: 0, backgroundColor: "#04305c" }} />
                <Text size="sm" fw={700} lh={1.55} c="#04305c">
                  Both parties can initiate collaboration discussions
                </Text>
              </Flex>
            </Stack>
          </SimpleGrid>
        </Box>
      </Paper>
    </>
  );
}
